import React, { useState, useEffect, useRef } from 'react';
import { usePlanFeatures } from '../../contexts/PlanFeaturesContext';
import './styles.css';

import config from '../../config/appConfig';
import { apicFetch } from '../../services/apiTokenService';
import { useModalFocusReturn } from '../../hooks/useModalFocusReturn';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { getErrorMessage, ERROR_TYPES } from '../errorMessages/errorMessages.constants';
import { classifyApiError } from '../../utils/apiErrorClassifier';

const IncorrectInfoModal = ({ isOpen, onClose, provider }) => {
    const { getFooterLinks, getErrorContactUsUrl } = usePlanFeatures();
    const [selectedIssues, setSelectedIssues] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionStatus, setSubmissionStatus] = useState(null); // 'success' or 'error'
    const modalRef = useRef(null);

    // Restore focus to whatever triggered this modal once it closes (declared before
    // the effects below so it captures the trigger element BEFORE they move focus
    // onto the close button).
    useModalFocusReturn(isOpen);

    // Lock page scroll while open, without jumping to the top and correctly restoring
    // the exact scroll position on close.
    useBodyScrollLock(isOpen);

    const footerLinks = getFooterLinks();
    // Provider-facing "update your info" form link (bottom "Health care providers"
    // prompt) — intentionally NOT the generic plan Contact Us page; this is a
    // dedicated form providers use to self-report/update their own listing.
    const providerUpdateFormUrl = 'https://medicaidportal.aetna.com/mcainteractiveforms/ProviderForms/ProviderRequestForm.aspx';
    // Plan-aware Contact Us link used only for the SMTP submission-error state below
    // (see reports/SPECIFIC_ERROR_MESSAGES_PLAN.md) — distinct from the provider
    // update form link above.
    const contactUsUrl = getErrorContactUsUrl();

    const providerName = provider 
        ? `${provider.firstName || ''} ${provider.lastName || ''}`.trim() || 'this provider'
        : 'this provider';

    useEffect(() => {
        if (isOpen) {
            // Reset states when modal opens
            setSelectedIssues([]);
            setSubmissionStatus(null);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && modalRef.current) {
            const focusableElements = modalRef.current.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            const handleTabKey = (e) => {
                if (e.key === 'Tab') {
                    if (e.shiftKey && document.activeElement === firstElement) {
                        e.preventDefault();
                        lastElement?.focus();
                    } else if (!e.shiftKey && document.activeElement === lastElement) {
                        e.preventDefault();
                        firstElement?.focus();
                    }
                }
            };

            const handleEscapeKey = (e) => {
                if (e.key === 'Escape') {
                    onClose();
                }
            };

            modalRef.current.addEventListener('keydown', handleTabKey);
            document.addEventListener('keydown', handleEscapeKey);

            // Focus close button
            const closeButton = modalRef.current.querySelector('.incorrect-info-modal-close');
            closeButton?.focus({ preventScroll: true });

            return () => {
                modalRef.current?.removeEventListener('keydown', handleTabKey);
                document.removeEventListener('keydown', handleEscapeKey);
            };
        }
    }, [isOpen, onClose]);

    const handleOverlayClick = (e) => {
        if (e.target.classList.contains('incorrect-info-modal-overlay')) {
            onClose();
        }
    };

    const handleCheckboxChange = (issue) => {
        setSelectedIssues(prev => {
            if (prev.includes(issue)) {
                return prev.filter(i => i !== issue);
            } else {
                return [...prev, issue];
            }
        });
    };

    const handleSubmit = async () => {
        if (selectedIssues.length === 0 || isSubmitting) return;

        let selectedIssuesData = {};

        selectedIssues.forEach((issue) => {
            selectedIssuesData[issue] = issue === 'Address' ? provider?.selectedLocation.formattedAddress ? provider?.selectedLocation.formattedAddress : 'NA' 
                : issue === 'Hours of operation' ? [
                    `Monday: ${provider?.selectedLocation.fromMonday} to ${provider?.selectedLocation.toMonday}`,
                    `Tuesday: ${provider?.selectedLocation.fromTuesday} to ${provider?.selectedLocation.toTuesday}`,
                    `Wednesday: ${provider?.selectedLocation.fromWednesday} to ${provider?.selectedLocation.toWednesday}`,
                    `Thursday: ${provider?.selectedLocation.fromThursday} to ${provider?.selectedLocation.toThursday}`,
                    `Friday: ${provider?.selectedLocation.fromFriday} to ${provider?.selectedLocation.toFriday}`,
                    `Saturday: ${provider?.selectedLocation.fromSaturday} to ${provider?.selectedLocation.toSaturday}`,
                    `Sunday: ${provider?.selectedLocation.fromSunday} to ${provider?.selectedLocation.toSunday}`
                ].join(', ')
                : issue === 'Phone number' ? provider?.selectedLocation.phone 
                : issue === 'Website' ? provider?.selectedLocation.url 
                : issue === 'Accepting my insurance' ? 'NA' 
                : issue === 'Accepting new patients' ? provider?.selectedLocation.newAssigns === '-1' ? 'Yes' : 'No'
                : issue === 'Virtual care' ? provider?.selectedLocation.telehealth 
                : issue === 'Handicap accessible' ? provider?.selectedLocation?.ADA?.handicapAccess
                : issue === 'Easy access to public transportation' ? provider?.selectedLocation.ADA.pubTrans 
                : issue === 'Languages spoken' ? provider?.language : 
                ''
            });
        setIsSubmitting(true);

        try {
            // Prepare feedback data matching backend IncorrectInfoRequest structure
            const feedbackData = {
                providerId: provider?.provId || provider?.providerID || '',
                providerName: providerName || '',
                providerAddress: provider?.selectedLocation?.formattedAddress || provider?.locations?.[0]?.formattedAddress || '',
                locationName: provider?.selectedLocation?.affiliation || provider?.locations?.[0]?.affiliation || '',
                incorrectCategories: selectedIssuesData,
                pageSource: 'provider-search-results',
                environmentId: provider?.environmentId || 'prod'
            };

            // Send feedback to the provider feedback API
            const feedbackUrl = (import.meta.env.DEV && window.location.hostname === 'localhost')
              ? `/api-proxy/hcb/v3/providersearchresults/providers/feedback`
              : `${config.APIC_BASE_URL_API}/hcb/v3/providersearchresults/providers/feedback`;
            const response = await apicFetch(feedbackUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(feedbackData)
            });

            if (!response.ok) {
                throw new Error('Failed to submit feedback');
            }

            // Success
            setSubmissionStatus('success');
        } catch (error) {
            console.error('Error submitting incorrect info:', classifyApiError({ error }), error);
            setSubmissionStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const issueOptions = [
        ['Accepting my insurance', 'Accepting new patients'],
        ['Address', 'Hours of operation', 'Phone number'],
        ['Website', 'Virtual care', 'Handicap accessible'],
        ['Easy access to public transportation', 'Languages spoken']
    ];

    // Success state
    if (submissionStatus === 'success') {
        return (
            <div 
                className="incorrect-info-modal-overlay"
                onClick={handleOverlayClick}
                role="dialog"
                aria-modal="true"
                aria-labelledby="incorrect-info-modal-title"
            >
                <div className="incorrect-info-modal" ref={modalRef}>
                    <button 
                        className="incorrect-info-modal-close"
                        onClick={onClose}
                        aria-label="Close modal"
                        type="button"
                    >
                        ×
                    </button>
                    
                    <div className="incorrect-info-modal-content">
                        <h2 id="incorrect-info-modal-title" className="incorrect-info-modal-title">
                            We got your message.
                        </h2>
                        
                        <p className="incorrect-info-modal-message">
                            Thank you for telling us about the incorrect info. It helps us serve you better. We'll follow up with the provider and correct the info as soon as possible.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (submissionStatus === 'error') {
        return (
            <div 
                className="incorrect-info-modal-overlay"
                onClick={handleOverlayClick}
                role="dialog"
                aria-modal="true"
                aria-labelledby="incorrect-info-modal-title"
            >
                <div className="incorrect-info-modal" ref={modalRef}>
                    <button 
                        className="incorrect-info-modal-close"
                        onClick={onClose}
                        aria-label="Close modal"
                        type="button"
                    >
                        ×
                    </button>
                    
                    <div className="incorrect-info-modal-content">
                        <h2 id="incorrect-info-modal-title" className="incorrect-info-modal-title">
                            We didn't get your message.
                        </h2>
                        
                        <p className="incorrect-info-modal-message">
                            {getErrorMessage(ERROR_TYPES.EMAIL)} Need help? <a 
                                href={contactUsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="incorrect-info-contact-link"
                            >
                                Contact us
                            </a>.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Default form state
    return (
        <div 
            className="incorrect-info-modal-overlay"
            onClick={handleOverlayClick}
            role="dialog"
            aria-modal="true"
            aria-labelledby="incorrect-info-modal-title"
        >
            <div className="incorrect-info-modal" ref={modalRef}>
                <button 
                    className="incorrect-info-modal-close"
                    onClick={onClose}
                    aria-label="Close modal"
                    type="button"
                >
                    ×
                </button>
                
                <div className="incorrect-info-modal-content">
                    <h2 id="incorrect-info-modal-title" className="incorrect-info-modal-title">
                        Tell us about incorrect info
                    </h2>
                    
                    <p className="incorrect-info-modal-message">
                        Did you find incorrect info about {providerName}? Let us know. You can help us make this tool better. Tell us what info is incorrect. We'll follow up with the provider and get the right info.
                    </p>

                    <fieldset className="incorrect-info-modal-question" role="group" aria-labelledby="incorrect-info-question-label">
                        <legend id="incorrect-info-question-label">
                            What info is incorrect?
                        </legend>

                        <ul className="incorrect-info-modal-options">
                            {issueOptions.map((row, rowIndex) => (
                                <li key={rowIndex} className="incorrect-info-modal-row">
                                    {row.map((issue) => (
                                        <label key={issue} className="incorrect-info-checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={selectedIssues.includes(issue)}
                                                onChange={() => handleCheckboxChange(issue)}
                                                className="incorrect-info-checkbox"
                                                aria-checked={selectedIssues.includes(issue)}
                                            />
                                            <span className="incorrect-info-checkbox-text">{issue}</span>
                                        </label>
                                    ))}
                                </li>
                            ))}
                        </ul>
                    </fieldset>

                    <button 
                        className="incorrect-info-submit-button"
                        onClick={handleSubmit}
                        disabled={selectedIssues.length === 0 || isSubmitting}
                        type="button"
                    >
                        {isSubmitting ? 'Sending...' : 'Send incorrect info'}
                    </button>

                    <p className="incorrect-info-provider-message">
                        <span className="incorrect-info-provider-message-bold">Health care providers: </span>
                        <span className="incorrect-info-provider-message-regular">Is your info incorrect? You can help members by updating it. </span>
                        <a 
                            href={providerUpdateFormUrl}
                            target="_self"
                            rel="noopener noreferrer"
                            className="incorrect-info-contact-link"
                        >
                            Contact us
                        </a>
                        <span className="incorrect-info-provider-message-regular">.</span>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default IncorrectInfoModal;
